import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Gavel, Server, ShieldAlert } from 'lucide-react';
import { api } from '../../api/cliente.js';
import { Botao } from '../../componentes/ui/Botao.js';
import { Pill, type TomPill } from '../../componentes/ui/Pill.js';
import { ModalAdicionarNo } from './ModalAdicionarNo.js';
import { ModalDetalhesNo } from './ModalDetalhesNo.js';
import { ModalRemoverNo } from './ModalRemoverNo.js';

interface No {
  id: string;
  nome: string;
  hostname: string;
  ipPublico: string;
  ipMalha: string;
  papel: 'DADOS' | 'ARBITRO';
  status: string;
  provisionStatus: string;
  versaoPdns: string | null;
  versaoStack: string | null;
  ultimoContato: string | null;
}

interface SaudeCluster {
  estado: string;
  tamanho: number;
  aceitaEscrita: boolean;
  versaoProvider: string;
}

export function Servidores() {
  const qc = useQueryClient();
  const [modalAberto, setModalAberto] = useState(false);
  const [detalhes, setDetalhes] = useState<No | null>(null);
  const [removendo, setRemovendo] = useState<No | null>(null);

  const { data: nos = [], isLoading } = useQuery({
    queryKey: ['infra', 'nos'],
    queryFn: () => api.get<No[]>('/infra/nos'),
  });

  const { data: cluster } = useQuery({
    queryKey: ['cluster', 'saude'],
    queryFn: () => api.get<SaudeCluster>('/cluster/saude'),
    refetchInterval: 15_000,
  });

  const dados = nos.filter((n) => n.papel === 'DADOS');
  const arbitros = nos.filter((n) => n.papel === 'ARBITRO');

  // Membros do grupo = nós de dados + árbitros. Quorum exige mais da metade.
  const membros = dados.length + arbitros.length;
  const quorumMinimo = Math.floor(membros / 2) + 1;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[14px] font-bold">
            {dados.length} {dados.length === 1 ? 'nó de dados' : 'nós de dados'}
            {arbitros.length > 0 && ` · ${arbitros.length} árbitro`}
          </p>
          <p className="text-[12.5px] text-texto-suave">
            {membros} membros no grupo — {quorumMinimo} precisam estar de pé para o painel gravar
          </p>
        </div>
        <Botao variante="primario" onClick={() => setModalAberto(true)}>
          Adicionar nó
        </Botao>
      </div>

      {cluster && (
        <div
          className={`flex items-start gap-3 rounded-card border px-4 py-3 ${
            cluster.aceitaEscrita
              ? 'border-sucesso/20 bg-sucesso-fundo text-sucesso'
              : 'border-erro/20 bg-erro-fundo text-erro'
          }`}
        >
          {cluster.aceitaEscrita ? <Server size={18} className="mt-0.5" /> : <ShieldAlert size={18} className="mt-0.5" />}
          <div className="text-[13px]">
            <p className="font-extrabold">
              {cluster.aceitaEscrita
                ? `Cluster consistente — ${cluster.tamanho} membros conectados`
                : 'Cluster sem quorum — painel em somente leitura'}
            </p>
            <p className="mt-0.5 opacity-90">
              Galera {cluster.versaoProvider}.{' '}
              {cluster.aceitaEscrita
                ? 'O DNS e a escrita estão operando normalmente.'
                : 'O DNS continua respondendo; apenas alterações estão bloqueadas.'}
            </p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-card border border-borda bg-superficie" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {nos.map((no) => (
            <CartaoNo
              key={no.id}
              no={no}
              aoAbrirDetalhes={() => setDetalhes(no)}
              aoRemover={() => setRemovendo(no)}
            />
          ))}
        </div>
      )}

      <ModalAdicionarNo
        aberto={modalAberto}
        aoFechar={() => setModalAberto(false)}
        aoConcluir={() => {
          void qc.invalidateQueries({ queryKey: ['infra', 'nos'] });
          void qc.invalidateQueries({ queryKey: ['cluster'] });
        }}
        ipsMalhaEmUso={nos.map((n) => n.ipMalha)}
      />

      {detalhes && (
        <ModalDetalhesNo
          noId={detalhes.id}
          nome={detalhes.nome}
          hostname={detalhes.hostname}
          aoFechar={() => setDetalhes(null)}
        />
      )}

      {removendo && (
        <ModalRemoverNo
          noId={removendo.id}
          nome={removendo.nome}
          papel={removendo.papel}
          aoFechar={() => setRemovendo(null)}
          aoRemovido={() => {
            void qc.invalidateQueries({ queryKey: ['infra', 'nos'] });
            void qc.invalidateQueries({ queryKey: ['cluster'] });
          }}
        />
      )}
    </div>
  );
}

function CartaoNo({
  no,
  aoAbrirDetalhes,
  aoRemover,
}: {
  no: No;
  aoAbrirDetalhes: () => void;
  aoRemover: () => void;
}) {
  const arbitro = no.papel === 'ARBITRO';

  const tom: TomPill =
    no.provisionStatus === 'OK'
      ? 'ok'
      : no.provisionStatus === 'FALHA'
        ? 'erro'
        : no.provisionStatus === 'EM_ANDAMENTO'
          ? 'aviso'
          : 'neutro';

  const rotulo =
    no.provisionStatus === 'OK'
      ? 'Provisionado'
      : no.provisionStatus === 'FALHA'
        ? 'Falha na instalação'
        : no.provisionStatus === 'EM_ANDAMENTO'
          ? 'Instalando'
          : 'Pendente';

  return (
    <article className="rounded-card border border-borda bg-superficie p-5 transition-shadow hover:shadow-hover">
      <div className="flex items-start gap-3">
        <span
          className={`grid size-10 shrink-0 place-items-center rounded-chip ${
            arbitro ? 'bg-borda-suave text-texto-suave' : 'bg-info-fundo text-info'
          }`}
        >
          {arbitro ? <Gavel size={19} /> : <Server size={19} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-extrabold leading-tight">{no.nome}</p>
          <p className="truncate font-mono text-[12px] text-texto-suave">{no.hostname}</p>
        </div>
        <Pill tom={tom}>{rotulo}</Pill>
      </div>

      <dl className="mt-4 space-y-1.5 text-[12.5px]">
        <Linha rotulo="IP público" valor={no.ipPublico} mono />
        <Linha rotulo="Malha" valor={no.ipMalha} mono />
        <Linha rotulo="Papel" valor={arbitro ? 'Árbitro (só quorum)' : 'Dados'} />
        {no.versaoPdns && !arbitro && <Linha rotulo="PowerDNS" valor={no.versaoPdns} mono />}
        {no.versaoStack && <Linha rotulo="Stack" valor={no.versaoStack} mono />}
      </dl>

      {arbitro && (
        // Vale dizer explicitamente: alguém olhando o painel pode estranhar um
        // membro do cluster sem banco nem DNS e achar que está quebrado.
        <p className="mt-3 rounded-chip bg-borda-tenue px-3 py-2 text-[11.5px] leading-snug text-texto-suave">
          Participa do quorum sem guardar dados. Existe para que a perda de um nó
          de dados não bloqueie a escrita no painel.
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <Botao onClick={aoAbrirDetalhes} className="flex-1">
          Ver detalhes
        </Botao>
        <Botao variante="perigo" onClick={aoRemover} title="Remover este nó do cluster">
          Remover
        </Botao>
      </div>
    </article>
  );
}

function Linha({ rotulo, valor, mono }: { rotulo: string; valor: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="shrink-0 text-texto-suave">{rotulo}</dt>
      <dd className={`truncate text-right font-semibold ${mono ? 'font-mono' : ''}`}>{valor}</dd>
    </div>
  );
}
